/// <reference types="vite/client" />
import {
  HeadContent,
  Scripts,
  createRootRouteWithContext,
  Outlet,
  useRouterState,
} from '@tanstack/react-router';
import * as React from 'react';
import { IntlayerProvider } from 'react-intlayer';

import { createRootHeadConfig } from './constants';
import { beforeRootLoad } from './utils';

import { Footer } from '~/components/Footer';
import { Header } from '~/components/Header';
import { ThemeProvider } from '~/components/theme';
import type { RouterContext } from '~/router';
import appCss from '~/styles/app.css?url';
import { DEFAULT_LOCALE, isAppLocale, type AppLocale } from '~/utils/i18n';
import '~/dictionaries';


const RootDocument = () => {
  // useRouterState with selector subscribes to location changes (reactive)
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Extract locale from URL (e.g., "/en/page" → "en")
  const urlLocale = pathname.split('/')[1];
  const locale = isAppLocale(urlLocale) ? urlLocale : DEFAULT_LOCALE;

  return (
    <html lang={locale} suppressHydrationWarning>
      <head>
        <meta charSet="UTF-8" />
        {/* Anti-flash script: apply theme class before paint to prevent FOUC */}
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(){
              try {
                var t = document.cookie.match(/trypillian-theme=([^;]+)/);
                var theme = t ? t[1] : null;
                if (theme === 'dark') {
                  document.documentElement.classList.add('dark');
                } else if (theme === 'light') {
                  document.documentElement.classList.remove('dark');
                } else if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
                  document.documentElement.classList.add('dark');
                }
              } catch(e) {}
            })();`,
          }}
        />
        <HeadContent />
      </head>
      <body suppressHydrationWarning>
        <ThemeProvider
          attribute="class"
          defaultTheme="system"
          enableSystem
          disableTransitionOnChange
        >
          <IntlayerProvider
            key={locale}
            defaultLocale={DEFAULT_LOCALE}
            locale={locale}
          >
            <div className="min-h-screen flex flex-col">
              <Header />
              <Outlet />
              <Footer />
            </div>
          </IntlayerProvider>
        </ThemeProvider>
        <Scripts />
      </body>
    </html>
  );
};

type AllRouteParams = {
  locale?: AppLocale;
};

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: beforeRootLoad,
  head: (context) => {
    const params = context.params as AllRouteParams;
    const locale = params.locale ?? DEFAULT_LOCALE;

    return createRootHeadConfig({
      locale,
      appStylesHref: appCss,
    });
  },
  component: RootDocument,
});
